import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import RestoreIcon from "@mui/icons-material/Restore";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { format, parseISO } from "date-fns";
import {
  InvestmentProject,
  INVESTMENT_ARCHIVE_REASON_LABELS,
  INVESTMENT_PROJECT_STATUS_LABELS,
} from "../../../entities/admin-investments";
import {
  getCaliberColor,
  renderCaliberShortName,
} from "../../../shared/lib/caliber";
import { ResidentCaliber } from "../../../entities/admin-events";

interface InvestmentsProjectViewDialogProps {
  open: boolean;
  investment: InvestmentProject | null;
  isLoading: boolean;
  isActing: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onComplete?: () => void;
  onRemove?: () => void;
  onRestore?: () => void;
  onHide?: () => void;
  onUnhide?: () => void;
}

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  try {
    return format(parseISO(value), "dd.MM.yyyy HH:mm");
  } catch {
    return value;
  }
};

const InfoRow = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2">{value || value === 0 ? String(value) : "—"}</Typography>
  </Box>
);

export const InvestmentsProjectViewDialog = ({
  open,
  investment,
  isLoading,
  isActing,
  onClose,
  onEdit,
  onComplete,
  onRemove,
  onRestore,
  onHide,
  onUnhide,
}: InvestmentsProjectViewDialogProps) => {
  if (!investment && !isLoading) return null;

  const isActive = investment?.status === "active";
  const isArchived = investment?.status === "archived";
  const author = investment?.author;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Инвестиционный проект</DialogTitle>
      <DialogContent>
        {isLoading || !investment ? (
          <Typography color="text.secondary">Загрузка...</Typography>
        ) : (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={INVESTMENT_PROJECT_STATUS_LABELS[investment.status]}
                color={isActive ? "success" : "default"}
              />
              <Chip
                size="small"
                label={investment.isExternal ? "Внешний" : "Резидентский"}
                color={investment.isExternal ? "primary" : "default"}
              />
              {investment.isHidden && (
                <Chip size="small" color="warning" label="Скрыт админом" />
              )}
              {investment.archiveReason && (
                <Chip
                  size="small"
                  label={INVESTMENT_ARCHIVE_REASON_LABELS[investment.archiveReason]}
                />
              )}
              {isArchived && (
                <Chip
                  size="small"
                  label={
                    investment.isPublicArchiveVisible
                      ? "Публичный архив"
                      : "Скрытый архив"
                  }
                />
              )}
            </Stack>

            {author && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Avatar src={author.avatarUrl} alt={author.firstName}>
                  {author.firstName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2">
                    {author.firstName} {author.lastName}
                  </Typography>
                  <Chip
                    size="small"
                    sx={{ mt: 0.5 }}
                    color={getCaliberColor(author.caliber as ResidentCaliber)}
                    label={renderCaliberShortName(author.caliber as ResidentCaliber)}
                  />
                </Box>
              </Stack>
            )}

            {investment.logoUrl && (
              <Box
                component="img"
                src={investment.logoUrl}
                alt={investment.title}
                sx={{
                  width: 96,
                  height: 96,
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            )}

            <Typography variant="h6">{investment.title}</Typography>
            <Typography variant="body2" whiteSpace="pre-wrap">
              {investment.description || "—"}
            </Typography>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="Лайки" value={investment.likeCount} />
              <InfoRow label="Дизлайки" value={investment.dislikeCount} />
              <InfoRow label="Источник" value={investment.externalSource} />
              <InfoRow label="Заявка" value={investment.applicationId} />
            </Stack>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="ФИО основателя" value={investment.founderFullName} />
              <InfoRow label="Email" value={investment.founderEmail} />
              <InfoRow label="Телефон" value={investment.founderPhone} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="Telegram" value={investment.founderTelegram} />
              <InfoRow label="WhatsApp" value={investment.founderWhatsapp} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Сайт
                </Typography>
                {investment.website ? (
                  <Link href={investment.website} target="_blank" rel="noopener" display="block">
                    {investment.website}
                  </Link>
                ) : (
                  <Typography variant="body2">—</Typography>
                )}
              </Box>
            </Stack>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Video Pitch Deck
              </Typography>
              {investment.videoPitchUrl ? (
                <Link
                  href={investment.videoPitchUrl}
                  target="_blank"
                  rel="noopener"
                  display="block"
                >
                  {investment.videoPitchUrl}
                </Link>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                Pitch Deck
              </Typography>
              {investment.pitchDeckUrl ? (
                <Link
                  href={investment.pitchDeckUrl}
                  target="_blank"
                  rel="noopener"
                  display="block"
                >
                  Открыть PDF
                </Link>
              ) : (
                <Typography variant="body2">—</Typography>
              )}
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <InfoRow label="Опубликован" value={formatDate(investment.publishedAt)} />
              <InfoRow label="Архивирован" value={formatDate(investment.archivedAt)} />
              <InfoRow label="Создан" value={formatDate(investment.createdAt)} />
              <InfoRow label="Обновлён" value={formatDate(investment.updatedAt)} />
            </Stack>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        {investment?.isExternal && isActive && onEdit && (
          <Button startIcon={<EditIcon />} onClick={onEdit} disabled={isActing}>
            Редактировать
          </Button>
        )}
        {isActive && onComplete && (
          <Button
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={onComplete}
            disabled={isActing}
          >
            Исполнено
          </Button>
        )}
        {isActive && onRemove && (
          <Button
            color="error"
            startIcon={<UnpublishedIcon />}
            onClick={onRemove}
            disabled={isActing}
          >
            Снять с публикации
          </Button>
        )}
        {isArchived && onRestore && (
          <Button
            color="primary"
            startIcon={<RestoreIcon />}
            onClick={onRestore}
            disabled={isActing}
          >
            Восстановить
          </Button>
        )}
        {investment && !investment.isHidden && onHide && (
          <Button
            color="warning"
            startIcon={<VisibilityOffIcon />}
            onClick={onHide}
            disabled={isActing}
          >
            Скрыть
          </Button>
        )}
        {investment?.isHidden && onUnhide && (
          <Button
            startIcon={<VisibilityIcon />}
            onClick={onUnhide}
            disabled={isActing}
          >
            Показать
          </Button>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};
